import { useEffect, useMemo, useRef, useState } from "react"

import {
  useAdminUsers,
  useUserSubordinates,
  useAssignSubordinate,
  useUnassignSubordinate,
  type AdminUser,
} from "@/api/hooks/admin"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog"

function showToast(msg: string, type: "success" | "error" = "success") {
  const el = document.createElement("div")
  el.textContent = msg
  el.className = `fixed top-4 right-4 z-[9999] rounded-lg px-4 py-3 text-sm font-medium shadow-lg ${
    type === "error"
      ? "bg-red-900/90 text-red-200 border border-red-700"
      : "bg-green-900/90 text-green-200 border border-green-700"
  }`
  document.body.appendChild(el)
  setTimeout(() => el.remove(), 5000)
}

interface Props {
  user: AdminUser
  onClose: () => void
}

export default function SubordinatesModal({ user, onClose }: Props) {
  const { data: usersData } = useAdminUsers()
  const { data: subsData, refetch: refetchSubs } = useUserSubordinates(user.id)
  const assignSub = useAssignSubordinate()
  const unassignSub = useUnassignSubordinate()

  const [dropdownOpen, setDropdownOpen] = useState(false)
  const [search, setSearch] = useState("")
  const dropdownRef = useRef<HTMLDivElement>(null)
  const searchRef = useRef<HTMLInputElement>(null)

  const subordinates = useMemo(
    () => subsData?.data?.subordinates ?? [],
    [subsData],
  )
  const subordinateIds = useMemo(
    () => new Set(subordinates.map((s) => s.id)),
    [subordinates],
  )

  // Filter available users: not this team leader, not admin, not already assigned
  const availableUsers = useMemo(() => {
    const all = usersData?.data ?? []
    return all.filter(
      (u) =>
        u.id !== user.id && u.role !== "admin" && !subordinateIds.has(u.id),
    )
  }, [usersData, user.id, subordinateIds])

  const filteredAvailable = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return availableUsers
    return availableUsers.filter((u) => {
      const haystack = `${u.username} ${u.role}`.toLowerCase()
      return haystack.includes(q)
    })
  }, [availableUsers, search])

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        setDropdownOpen(false)
        setSearch("")
      }
    }
    document.addEventListener("mousedown", handleClick, true)
    return () => document.removeEventListener("mousedown", handleClick, true)
  }, [])

  useEffect(() => {
    if (dropdownOpen) {
      queueMicrotask(() => searchRef.current?.focus())
    }
  }, [dropdownOpen])

  function handleAssign(subordinateId: number) {
    assignSub.mutate(
      { teamLeaderId: user.id, subordinateId },
      {
        onSuccess: () => refetchSubs(),
        onError: (err) =>
          showToast(
            err instanceof Error ? err.message : "Error assigning subordinate",
            "error",
          ),
      },
    )
  }

  function handleUnassign(subordinateId: number) {
    if (
      !window.confirm(
        "Are you sure you want to unassign this subordinate?",
      )
    )
      return
    unassignSub.mutate(
      { teamLeaderId: user.id, subordinateId },
      {
        onSuccess: () => refetchSubs(),
        onError: (err) =>
          showToast(
            err instanceof Error
              ? err.message
              : "Error unassigning subordinate",
            "error",
          ),
      },
    )
  }

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-[700px] border-[#4a4a4a] bg-[#3a3a3a] text-white">
        <DialogHeader>
          <DialogTitle>Manage Subordinates for {user.username}</DialogTitle>
          <DialogDescription className="text-white">
            Assign or unassign subordinates for this team leader.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Assigned subordinates chips */}
          <div>
            <label className="mb-2 block text-sm font-semibold">
              Assigned Subordinates
            </label>
            <div className="flex min-h-[50px] max-h-[200px] flex-wrap gap-2 overflow-y-auto rounded-md border border-[#555] bg-[#2d2d2d] p-2">
              {subordinates.length === 0 ? (
                <span className="py-2 text-sm text-white">
                  No subordinates assigned
                </span>
              ) : (
                subordinates.map((sub) => (
                  <span
                    key={sub.id}
                    className="inline-flex items-center gap-1 rounded-full bg-purple-700/20 px-2.5 py-1 text-xs font-medium text-purple-300"
                  >
                    {sub.username}{" "}
                    <span className="text-white">({sub.role})</span>
                    <button
                      onClick={() => handleUnassign(sub.id)}
                      className="ml-0.5 text-purple-300 hover:text-red-400"
                    >
                      &times;
                    </button>
                  </span>
                ))
              )}
            </div>
          </div>

          {/* Add subordinate dropdown */}
          <div>
            <label className="mb-2 block text-sm font-semibold">
              Add Subordinate
            </label>
            <div ref={dropdownRef} className="relative">
              <button
                type="button"
                onClick={() => {
                  // Clear the search when the dropdown closes
                  if (dropdownOpen) setSearch("")
                  setDropdownOpen(!dropdownOpen)
                }}
                className="flex w-full items-center justify-between rounded-md border border-[#555] bg-[#2d2d2d] px-3 py-2 text-sm text-white"
              >
                <span>Select subordinates...</span>
                <span className="text-xs">&#9660;</span>
              </button>

              {dropdownOpen && (
                <div className="absolute left-0 right-0 top-full z-50 mt-1 flex max-h-[320px] flex-col rounded-md border border-[#555] bg-[#2d2d2d] shadow-lg">
                  <div className="shrink-0 border-b border-[#444] p-2">
                    <input
                      ref={searchRef}
                      type="text"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder="Search users..."
                      aria-label="Search subordinates"
                      className="h-8 w-full rounded border border-[#555] bg-[#252525] px-2 text-xs text-white placeholder:text-[#888] focus:border-[#8b9aff] focus:outline-none"
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => e.stopPropagation()}
                    />
                  </div>
                  <div className="min-h-0 flex-1 overflow-y-auto p-1">
                    {availableUsers.length === 0 ? (
                      <div className="py-3 text-center text-sm text-white">
                        No users available
                      </div>
                    ) : filteredAvailable.length === 0 ? (
                      <div className="py-3 text-center text-sm text-[#888]">
                        No matches
                      </div>
                    ) : (
                      filteredAvailable.map((u) => (
                        <label
                          key={u.id}
                          className="flex cursor-pointer items-center gap-2 rounded px-3 py-2 text-left text-sm hover:bg-[#3a3a3a]"
                        >
                          <input
                            type="checkbox"
                            checked={false}
                            disabled={assignSub.isPending}
                            onChange={() => handleAssign(u.id)}
                            className="accent-[#8b9aff]"
                            aria-label={`Assign ${u.username}`}
                          />
                          <span className="font-medium text-white">
                            {u.username}
                          </span>
                          <span className="text-xs text-white">
                            ({u.role})
                          </span>
                        </label>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
            <p className="mt-2 text-xs text-white">
              Only mediabuyers and team leaders can be assigned as subordinates.
            </p>
          </div>

          {/* Footer — close only, all changes are live */}
          <div className="flex justify-end pt-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-[#7a808b]/45 bg-[#636b78] px-4 py-2 font-semibold text-white shadow-sm transition-colors hover:bg-[#58606c]"
            >
              Close
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
